import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Clock, ArrowLeft, Box } from "lucide-react";
import { fetchDisplayHome } from "../../api/public.api.js";
import { assetUrl } from "../../api/client.js";
import { placeholderImage, imgErrorFallback } from "../../lib/placeholders.js";
import { Container } from "../../components/layout/Container.jsx";
import { Breadcrumbs } from "../../components/shared/PageHero.jsx";
import { Lightbox } from "../../components/designs/Lightbox.jsx";
import { InquiryForm } from "../../components/forms/InquiryForm.jsx";
import { RichText } from "../../components/shared/RichText.jsx";
import { SEO } from "../../lib/seo.jsx";

const DAYS = [
  ["mon", "Monday"],
  ["tue", "Tuesday"],
  ["wed", "Wednesday"],
  ["thu", "Thursday"],
  ["fri", "Friday"],
  ["sat", "Saturday"],
  ["sun", "Sunday"],
];

export default function DisplayHomeDetailPage() {
  const { slug } = useParams();
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const {
    data: home,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["public", "display-home", slug],
    queryFn: () => fetchDisplayHome(slug),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (isError) return <Navigate to="/display-homes" replace />;
  if (isLoading || !home) {
    return (
      <Container className="py-20">
        <div className="h-72 animate-pulse rounded-2xl bg-bg-elevated" />
      </Container>
    );
  }

  const fallbackKey = home.slug ?? home._id ?? home.name;
  const realImages = home.images ?? [];
  const images =
    realImages.length > 0
      ? realImages.map((src) => assetUrl(src))
      : [
          placeholderImage("display", `${fallbackKey}-1`),
          placeholderImage("interior", `${fallbackKey}-2`),
          placeholderImage("display", `${fallbackKey}-3`),
          placeholderImage("interior", `${fallbackKey}-4`),
          placeholderImage("display", `${fallbackKey}-5`),
        ];

  return (
    <>
      <SEO
        title={home.name}
        description={
          home.description ??
          `Display home in ${home.locationGroup ?? home.address}. Featuring the ${home.linkedDesign?.name ?? "current"} design.`
        }
        image={images[0]}
      />

      <Container className="py-10 lg:py-12">
        <Breadcrumbs
          items={[{ label: "Display Homes", to: "/display-homes" }, { label: home.name }]}
          className="mb-6"
        />

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px] lg:gap-12">
          <div className="min-w-0 space-y-10">
            <header>
              <h1 className="font-display text-4xl text-text-primary lg:text-5xl">{home.name}</h1>
              {home.address && (
                <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-text-secondary">
                  <MapPin className="h-4 w-4 text-accent" />
                  {home.address}
                </p>
              )}
            </header>

            {/* Gallery */}
            {images.length > 0 && (
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
                <button
                  type="button"
                  onClick={() => setLightboxIdx(0)}
                  className="col-span-2 row-span-2 overflow-hidden rounded-2xl bg-bg-elevated"
                >
                  <img
                    src={images[0]}
                    onError={imgErrorFallback(`${home.slug}-0`)}
                    alt=""
                    className="aspect-square h-full w-full object-cover transition-transform hover:scale-[1.02] lg:aspect-auto"
                  />
                </button>
                {images.slice(1, 5).map((src, i) => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setLightboxIdx(i + 1)}
                    className="overflow-hidden rounded-xl bg-bg-elevated"
                  >
                    <img
                      src={src}
                      onError={imgErrorFallback(`${home.slug}-${i + 1}`)}
                      alt=""
                      loading="lazy"
                      className="aspect-square h-full w-full object-cover hover:scale-[1.04]"
                    />
                  </button>
                ))}
              </div>
            )}

            {home.description && (
              <section>
                <h2 className="font-display text-2xl text-text-primary">About this display</h2>
                <div className="mt-4">
                  <RichText html={home.description} />
                </div>
              </section>
            )}

            {/* Linked design */}
            {home.linkedDesign && (
              <section className="rounded-2xl border border-border-subtle bg-bg-elevated p-6">
                <p className="eyebrow">Featuring</p>
                <h3 className="mt-2 font-display text-2xl text-text-primary">
                  The {home.linkedDesign.name} design
                </h3>
                <p className="mt-1 text-sm text-text-secondary">
                  {home.linkedDesign.bedrooms} bed · {home.linkedDesign.bathrooms} bath
                  {home.linkedDesign.areaSq && ` · ${home.linkedDesign.areaSq} sq`}
                </p>
                {home.linkedDesign.slug && (
                  <Link
                    to={`/home-designs/${home.linkedDesign.slug}`}
                    className="mt-4 inline-block text-sm text-accent hover:text-accent-hover"
                  >
                    View design details →
                  </Link>
                )}
              </section>
            )}

            {/* Matterport 3D tour */}
            {home.matterportUrl && (
              <section className="rounded-2xl border border-accent/30 bg-accent/5 p-6 lg:p-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="eyebrow">3D virtual tour</p>
                    <h3 className="mt-2 font-display text-2xl text-text-primary">
                      Walk through this display online
                    </h3>
                    <p className="mt-1 text-sm text-text-secondary">
                      Explore every room without leaving the couch — full Matterport scan.
                    </p>
                  </div>
                  <a
                    href={home.matterportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-md bg-accent px-5 py-3 text-sm font-medium text-bg-base transition-colors hover:bg-accent-hover"
                  >
                    <Box className="h-4 w-4" />
                    Open 3D tour
                  </a>
                </div>
              </section>
            )}
          </div>

          {/* Sidebar — opening hours, directions, book a visit */}
          <aside className="lg:sticky lg:top-24 lg:self-start space-y-4">
            {/* Opening hours */}
            <div className="rounded-2xl border border-border-subtle bg-bg-elevated p-6">
              <p className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-text-muted">
                <Clock className="h-3.5 w-3.5" /> Opening hours
              </p>
              <ul className="mt-3 space-y-1 text-sm">
                {DAYS.map(([key, label]) => {
                  const value = home.openingHours?.[key];
                  return (
                    <li key={key} className="flex items-center justify-between">
                      <span className="text-text-secondary">{label}</span>
                      <span
                        className={
                          !value || value.toLowerCase() === "closed"
                            ? "text-text-muted"
                            : "text-text-primary"
                        }
                      >
                        {value || "Closed"}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {home.googleMapsUrl && (
                <a
                  href={home.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline mt-5 w-full text-sm"
                >
                  Get directions
                </a>
              )}
            </div>

            {/* Book a visit */}
            <div className="rounded-2xl border border-border-subtle bg-bg-elevated p-6">
              <p className="eyebrow">Book a visit</p>
              <h2 className="mt-2 font-display text-xl text-text-primary">Plan your tour</h2>
              <div className="mt-4">
                <InquiryForm
                  formType="appointment"
                  prefill={{
                    displayHome: home.name,
                    address: home.address,
                  }}
                />
              </div>
            </div>
          </aside>
        </div>

        <div className="mt-12">
          <Link
            to="/display-homes"
            className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-accent"
          >
            <ArrowLeft className="h-3 w-3" /> All display homes
          </Link>
        </div>
      </Container>

      {lightboxIdx !== null && (
        <Lightbox
          images={images}
          index={lightboxIdx}
          onChange={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </>
  );
}
